import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const target = event?.target;
    const details = this.#getDetailsElement(event);

    //If target is undefined, the main mobile header menu was opened.
    if(target === undefined) {

      //Make room for sticky header/announcement bar
      //Its possible to open the mobile header menu while scrolled down, with sticky header activated
      //Tell .menu-drawer what `top:` value to have in order to leave enough room for the sicky header/announcement bar
      const menuDrawer = this.querySelector(".menu-drawer");
      const header = document?.querySelector("#header-group header");

      //Math to figure out height of sticky header and distance between it and the top of the page
      const distanceFromTopOfPage = header?.getBoundingClientRect().top;
      const headerHeight = header?.offsetHeight;
      const distanceToAppend = (distanceFromTopOfPage + headerHeight) - 2;

      //Apply that value to the mobile header menu
      menuDrawer.style.setProperty('top', `${distanceToAppend}px`, 'important'); 
    }
    //Else, a child submenu was opened.
    else {

      if(target?.classList?.contains("menu-drawer__menu-item--mainlist")) {

        //Ensure we are scrolled to the top of the overflow of the menu that was just opened
        //Horizon child submenus can sometimes not be scrolled up all the way when the user clicks into them, if the user had scrolled down on the parent menu previous to it.
        const menuDrawer = event.target.closest(".menu-drawer");
              menuDrawer.scrollTop = 0;

        //Ensure the calculated height of submenu opened extends far down enough to cover utilities menu below it.
        //Get the <detail> submenu opened
        const detailsChild = details.querySelector(".menu-drawer__submenu");
        const detailsChildBottom = detailsChild?.getBoundingClientRect().bottom;

        //Get the full height of mobile menu slideout
        const mobileSlideoutHeight = document.querySelector(".menu-drawer").offsetHeight;

        //Calculate final height to apply to the opened submenu
        const finalHeight = detailsChildBottom - mobileSlideoutHeight;

        detailsChild.style.setProperty('height', `calc(100vh + ${finalHeight}px)`, 'important'); 
      }
    }

    //Open the menu or submenu
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => details.classList.add('menu-open'));

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    setTimeout(() => {
      reset(details);
      
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    }, 200);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
